import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  CalendarDays,
  Plus,
  Edit,
  Trash2,
  Save,
  ArrowLeft,
  Users,
  Check,
  Power
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSeasonsPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '' });

  useEffect(() => {
    if (!member?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSeasons();
  }, [member, navigate]);

  const fetchSeasons = async () => {
    try {
      const response = await axios.get(`${API_URL}/seasons`);
      setSeasons(response.data);
    } catch (error) {
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/seasons?admin_id=${member.id}`, {
        name: form.name,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString()
      });
      toast.success('Season created');
      setCreating(false);
      setForm({ name: '', start_date: '', end_date: '' });
      fetchSeasons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create season');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a season name');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/seasons/${editingSeason.id}?admin_id=${member.id}`, {
        name: form.name,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : undefined,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : undefined
      });
      toast.success('Season updated');
      setEditingSeason(null);
      setForm({ name: '', start_date: '', end_date: '' });
      fetchSeasons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update season');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (season) => {
    try {
      await axios.put(`${API_URL}/admin/seasons/${season.id}?admin_id=${member.id}`, {
        is_active: !season.is_active
      });
      toast.success(season.is_active ? `${season.name} deactivated` : `${season.name} is now active`);
      fetchSeasons();
    } catch (error) {
      toast.error('Failed to toggle season');
    }
  };

  const handleDelete = async (season) => {
    if (!window.confirm(`Delete "${season.name}"? This will unassign tournaments and remove all registrations.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/seasons/${season.id}?admin_id=${member.id}`);
      toast.success('Season deleted');
      fetchSeasons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEdit = (season) => {
    const startLocal = season.start_date ? new Date(season.start_date).toISOString().split('T')[0] : '';
    const endLocal = season.end_date ? new Date(season.end_date).toISOString().split('T')[0] : '';
    setForm({ name: season.name, start_date: startLocal, end_date: endLocal });
    setEditingSeason(season);
  };

  const openCreate = () => {
    setForm({ name: '', start_date: '', end_date: '' });
    setCreating(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <CalendarDays className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-heading text-white">Manage Seasons</h1>
            </div>
            <p className="text-muted-foreground">Create and manage tournament seasons</p>
          </div>
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-primary hover:bg-primary/90 rounded-pill px-6" data-testid="create-season-btn">
                <Plus className="mr-2 h-4 w-4" />
                New Season
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader>
                <DialogTitle className="font-heading">Create New Season</DialogTitle>
                <DialogDescription>Set a name and date range for the new season</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div className="space-y-2">
                  <Label>Season Name</Label>
                  <Input placeholder="e.g., 2026 Spring Season" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-muted border-border" data-testid="season-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-muted border-border" data-testid="season-start-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-muted border-border" data-testid="season-end-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                <Button onClick={handleCreate} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="season-save-btn">
                  {saving ? 'Creating...' : 'Create Season'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={!!editingSeason} onOpenChange={(open) => !open && setEditingSeason(null)}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Season</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 my-4">
              <div className="space-y-2">
                <Label>Season Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-muted border-border" data-testid="edit-season-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-muted border-border" data-testid="edit-season-start-input" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-muted border-border" data-testid="edit-season-end-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
              <Button onClick={handleUpdate} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="edit-season-save-btn">
                <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Season List */}
        {loading ? (
          <div className="text-center py-12"><div className="animate-pulse text-primary">Loading seasons...</div></div>
        ) : seasons.length === 0 ? (
          <Card className="bg-card border-white/10">
            <CardContent className="p-12 text-center">
              <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading text-white mb-2">No Seasons Yet</h3>
              <p className="text-muted-foreground">Create your first season to start tracking seasonal rankings.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {seasons.map((season, i) => (
              <Card key={season.id} className={`bg-card border-white/10 ${season.is_active ? 'border-primary/50' : ''}`} data-testid={`season-card-${i}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-heading text-white">{season.name}</h3>
                        {season.is_active && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">Active</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span>{formatDate(season.start_date)} — {formatDate(season.end_date)}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{season.registered_count} registered</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleActive(season)}
                        className={season.is_active ? 'border-green-500/50 text-green-400 hover:bg-green-500/20' : 'border-white/20 hover:bg-white/5'}
                        data-testid={`toggle-season-${i}`}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(season)} className="border-primary/50 hover:bg-primary/20" data-testid={`edit-season-${i}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(season)} className="border-destructive/50 hover:bg-destructive/20 text-destructive" data-testid={`delete-season-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
